// components/Users/Users.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, branchesAPI, departmentAPI } from '../services/api';
import { 
  User as UserIcon, Search, AlertCircle, Plus, Edit2, Trash2, Key, 
  Eye, EyeOff, MapPin, Briefcase, Filter, Users as UsersIcon,
  CheckCircle, XCircle, Shield, Building, ChefHat, Factory, FileText,
  MoreVertical, Mail, Phone, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

// Import UI Components
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Select } from '../components/UI/Select';
import { Modal } from '../components/UI/Modal';
import { LoadingSpinner } from '../components/UI/LoadingSpinner';
import { Badge } from '../components/UI/Badge';
import { Pagination } from '../components/UI/Pagination';

// Types
import { User as UserType, Branch, Department } from '../types/types';

const translations = {
  ar: {
    manage: 'إدارة المستخدمين',
    add: 'إضافة مستخدم',
    addFirst: 'إضافة أول مستخدم',
    noUsers: 'لا توجد مستخدمين',
    noMatch: 'لا توجد مستخدمين مطابقين',
    empty: 'لا توجد مستخدمين متاحين',
    searchPlaceholder: 'ابحث بالاسم أو اسم المستخدم...',
    role: 'الدور',
    allRoles: 'جميع الأدوار',
    admin: 'مدير',
    branch: 'فرع',
    chef: 'شيف',
    production: 'إنتاج',
    accountant: 'محاسب',
    previous: 'السابق',
    next: 'التالي',
    page: 'صفحة',
    of: 'من',
    username: 'اسم المستخدم',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    department: 'القسم',
    createdAt: 'تاريخ الإنشاء',
    updatedAt: 'تاريخ التحديث',
    edit: 'تعديل',
    resetPassword: 'تغيير كلمة المرور',
    delete: 'حذف',
    name: 'الاسم (عربي)',
    nameEn: 'الاسم (إنجليزي)',
    nameRequired: 'الاسم مطلوب',
    nameEnRequired: 'الاسم بالإنجليزية مطلوب',
    usernameRequired: 'اسم المستخدم مطلوب',
    passwordRequired: 'كلمة المرور مطلوبة',
    branchRequired: 'الفرع مطلوب',
    departmentRequired: 'القسم مطلوب',
    namePlaceholder: 'أدخل الاسم',
    nameEnPlaceholder: 'أدخل الاسم بالإنجليزية',
    usernamePlaceholder: 'أدخل اسم المستخدم',
    emailPlaceholder: 'أدخل البريد الإلكتروني',
    phonePlaceholder: 'أدخل رقم الهاتف',
    branchPlaceholder: 'اختر الفرع',
    departmentPlaceholder: 'اختر القسم',
    passwordPlaceholder: 'أدخل كلمة المرور',
    update: 'تحديث المستخدم',
    requiredFields: 'يرجى ملء جميع الحقول المطلوبة',
    usernameExists: 'اسم المستخدم مستخدم بالفعل',
    emailExists: 'البريد الإلكتروني مستخدم بالفعل',
    unauthorized: 'غير مصرح لك بالوصول',
    fetchError: 'حدث خطأ أثناء جلب البيانات',
    updateError: 'حدث خطأ أثناء التحديث',
    createError: 'حدث خطأ أثناء الإنشاء',
    added: 'تم إضافة المستخدم بنجاح',
    updated: 'تم تحديث المستخدم بنجاح',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    newPasswordPlaceholder: 'أدخل كلمة المرور الجديدة',
    confirmPasswordPlaceholder: 'أدخل تأكيد كلمة المرور',
    passwordMismatch: 'كلمة المرور وتأكيدها غير متطابقتين',
    passwordTooShort: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    passwordResetSuccess: 'تم إعادة تعيين كلمة المرور بنجاح',
    passwordResetError: 'حدث خطأ أثناء إعادة تعيين كلمة المرور',
    reset: 'إعادة تعيين',
    cancel: 'إلغاء',
    confirmDelete: 'تأكيد الحذف',
    deleteWarning: 'هل أنت متأكد من حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.',
    deleteError: 'حدث خطأ أثناء الحذف',
    deleted: 'تم الحذف بنجاح',
    profile: 'عرض التفاصيل',
    viewCurrentPassword: 'عرض كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    address: 'العنوان',
    city: 'المدينة',
    branchDetails: 'تفاصيل الفرع',
    permissions: 'الصلاحيات',
    filter: 'تصفية',
    clearFilters: 'مسح الفلاتر',
    totalUsers: 'إجمالي المستخدمين',
    users: 'مستخدمين',
    loading: 'جاري التحميل...',
    actions: 'الإجراءات',
    view: 'عرض',
    save: 'حفظ',
    close: 'إغلاق',
    confirm: 'تأكيد',
    success: 'نجاح',
    error: 'خطأ',
    warning: 'تحذير',
    info: 'معلومات',
    refresh: 'تحديث',
    roleFilter: 'تصفية حسب الدور',
    searchFilter: 'بحث',
    all: 'الكل',
    viewProfile: 'عرض الملف الشخصي',
    manageUser: 'إدارة المستخدم',
    lastLogin: 'آخر تسجيل دخول',
    details: 'التفاصيل',
    moreOptions: 'المزيد',
    export: 'تصدير',
    import: 'استيراد',
    bulkActions: 'إجراءات جماعية',
    selectAll: 'تحديد الكل',
    selected: 'محدد',
    items: 'عنصر',
    showing: 'عرض',
    to: 'إلى',
    results: 'نتيجة',
  },
  en: {
    manage: 'Manage Users',
    add: 'Add User',
    addFirst: 'Add First User',
    noUsers: 'No Users Found',
    noMatch: 'No Matching Users',
    empty: 'No Users Available',
    searchPlaceholder: 'Search by name or username...',
    role: 'Role',
    allRoles: 'All Roles',
    admin: 'Admin',
    branch: 'Branch',
    chef: 'Chef',
    production: 'Production',
    accountant: 'Accountant',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    of: 'of',
    username: 'Username',
    email: 'Email',
    phone: 'Phone',
    department: 'Department',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    edit: 'Edit',
    resetPassword: 'Change Password',
    delete: 'Delete',
    name: 'Name (Arabic)',
    nameEn: 'Name (English)',
    nameRequired: 'Name is required',
    nameEnRequired: 'English name is required',
    usernameRequired: 'Username is required',
    passwordRequired: 'Password is required',
    branchRequired: 'Branch is required',
    departmentRequired: 'Department is required',
    namePlaceholder: 'Enter name',
    nameEnPlaceholder: 'Enter name in English',
    usernamePlaceholder: 'Enter username',
    emailPlaceholder: 'Enter email',
    phonePlaceholder: 'Enter phone number',
    branchPlaceholder: 'Select branch',
    departmentPlaceholder: 'Select department',
    passwordPlaceholder: 'Enter password',
    update: 'Update User',
    requiredFields: 'Please fill all required fields',
    usernameExists: 'Username already exists',
    emailExists: 'Email already exists',
    unauthorized: 'Unauthorized access',
    fetchError: 'Error fetching data',
    updateError: 'Error updating user',
    createError: 'Error creating user',
    added: 'User added successfully',
    updated: 'User updated successfully',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    newPasswordPlaceholder: 'Enter new password',
    confirmPasswordPlaceholder: 'Enter confirm password',
    passwordMismatch: 'Password does not match',
    passwordTooShort: 'Password must be at least 6 characters',
    passwordResetSuccess: 'Password reset successfully',
    passwordResetError: 'Error resetting password',
    reset: 'Reset',
    cancel: 'Cancel',
    confirmDelete: 'Confirm Delete',
    deleteWarning: 'Are you sure you want to delete this user? This action cannot be undone.',
    deleteError: 'Error deleting user',
    deleted: 'Deleted successfully',
    profile: 'View Profile',
    viewCurrentPassword: 'View Password',
    currentPassword: 'Current Password',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    address: 'Address',
    city: 'City',
    branchDetails: 'Branch Details',
    permissions: 'Permissions',
    filter: 'Filter',
    clearFilters: 'Clear Filters',
    totalUsers: 'Total Users',
    users: 'users',
    loading: 'Loading...',
    actions: 'Actions',
    view: 'View',
    save: 'Save',
    close: 'Close',
    confirm: 'Confirm',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    refresh: 'Refresh',
    roleFilter: 'Filter by Role',
    searchFilter: 'Search',
    all: 'All',
    viewProfile: 'View Profile',
    manageUser: 'Manage User',
    lastLogin: 'Last Login',
    details: 'Details',
    moreOptions: 'More',
    export: 'Export',
    import: 'Import',
    bulkActions: 'Bulk Actions',
    selectAll: 'Select All',
    selected: 'Selected',
    items: 'items',
    showing: 'Showing',
    to: 'to',
    results: 'results',
  },
};

// Role Icons
const RoleIcon = ({ role, className = "w-4 h-4" }: { role: string; className?: string }) => {
  switch (role) {
    case 'admin':
      return <Shield className={`${className} text-purple-600`} />;
    case 'branch':
      return <Building className={`${className} text-blue-600`} />;
    case 'chef':
      return <ChefHat className={`${className} text-amber-600`} />;
    case 'production':
      return <Factory className={`${className} text-green-600`} />;
    case 'accountant':
      return <FileText className={`${className} text-emerald-600`} />;
    default:
      return <UserIcon className={`${className} text-gray-600`} />;
  }
};

// Role Colors
const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin': return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' };
    case 'branch': return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
    case 'chef': return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    case 'production': return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
    case 'accountant': return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
    default: return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
  }
};

// Role Options
const roleOptions = [
  { value: 'all', label: translations.ar.allRoles },
  { value: 'admin', label: translations.ar.admin },
  { value: 'branch', label: translations.ar.branch },
  { value: 'chef', label: translations.ar.chef },
  { value: 'production', label: translations.ar.production },
  { value: 'accountant', label: translations.ar.accountant },
];

// Format Date
const formatDate = (dateString: string, isRtl: boolean) => {
  const date = new Date(dateString);
  return isRtl 
    ? date.toLocaleDateString('ar-EG', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
};

export const Users: React.FC = () => {
  const { language } = useLanguage();
  const { user: loggedInUser } = useAuth();
  const isRtl = language === 'ar';
  const t = translations[language];
  
  // States
  const [users, setUsers] = useState<UserType[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  
  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  // Form States
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    username: '',
    email: '',
    phone: '',
    role: 'admin' as 'admin' | 'branch' | 'chef' | 'production' | 'accountant',
    branch: '',
    department: '',
    password: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [resetPasswordData, setResetPasswordData] = useState({ 
    password: '', 
    confirmPassword: '' 
  });
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});

  // Fetch Data
  const fetchData = useCallback(async () => {
    if (!loggedInUser || loggedInUser.role !== 'admin') {
      setError(t.unauthorized);
      setLoading(false);
      toast.error(t.unauthorized);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page,
        limit: 12,
        role: filterRole === 'all' ? undefined : filterRole,
        search: searchTerm || undefined,
      };

      const [usersResponse, branchesResponse, departmentsResponse] = await Promise.all([
        usersAPI.getAll(params),
        branchesAPI.getAll(),
        departmentAPI.getAll(),
      ]);
      
      // Handle users response
      if (usersResponse?.success) {
        const fetchedUsers = Array.isArray(usersResponse.data) ? usersResponse.data : [];
        setUsers(fetchedUsers);
        setTotalPages(usersResponse.totalPages || 1);
        setTotalUsers(usersResponse.total || fetchedUsers.length);
      } else if (Array.isArray(usersResponse)) {
        // Fallback for old API format
        setUsers(usersResponse);
        setTotalPages(Math.ceil(usersResponse.length / 12));
        setTotalUsers(usersResponse.length);
      }
      
      // Handle branches response
      if (branchesResponse?.success) {
        setBranches(Array.isArray(branchesResponse.data) ? branchesResponse.data : []);
      } else if (Array.isArray(branchesResponse)) {
        setBranches(branchesResponse);
      }
      
      // Handle departments response
      if (departmentsResponse?.success) {
        setDepartments(Array.isArray(departmentsResponse.data) ? departmentsResponse.data : []);
      } else if (Array.isArray(departmentsResponse?.data)) {
        setDepartments(departmentsResponse.data);
      }
      
    } catch (err: any) {
      console.error('Fetch error:', err);
      const errorMessage = err.response?.data?.message || err.message || t.fetchError;
      setError(errorMessage);
      if (!err.response) {
        toast.error(t.fetchError);
      }
    } finally {
      setLoading(false);
    }
  }, [loggedInUser, filterRole, page, searchTerm, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users
  const filteredUsers = useMemo(() => {
    if (!searchTerm && filterRole === 'all') return users;
    
    return users.filter(user => {
      const matchesSearch = searchTerm 
        ? (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.phone?.includes(searchTerm))
        : true;
      
      const matchesRole = filterRole !== 'all' ? user.role === filterRole : true;
      
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, filterRole]);

  // Handle search with debounce
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  // Handle role filter
  const handleRoleFilter = useCallback((value: string) => {
    setFilterRole(value);
    setPage(1);
  }, []);

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('all');
    setPage(1);
    setSelectedUsers([]);
  };

  // Open Add Modal
  const openAddModal = () => {
    setFormData({
      name: '',
      nameEn: '',
      username: '',
      email: '',
      phone: '',
      role: 'admin',
      branch: '',
      department: '',
      password: '',
      isActive: true,
    });
    setIsEditMode(false);
    setSelectedUser(null);
    setIsModalOpen(true);
    setFormErrors({});
    setError('');
  };

  // Open Edit Modal
  const openEditModal = (user: UserType) => {
    setFormData({
      name: user.name || '',
      nameEn: user.nameEn || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role,
      branch: user.branch?._id || user.branchId || '',
      department: user.department?._id || user.departmentId || '',
      password: '',
      isActive: user.isActive ?? true,
    });
    setIsEditMode(true);
    setSelectedUser(user);
    setIsModalOpen(true);
    setFormErrors({});
    setError('');
  };

  // Open Profile Modal
  const openProfileModal = (user: UserType) => {
    setSelectedUser(user);
    setIsProfileModalOpen(true);
  };

  // Open Reset Password Modal
  const openResetPasswordModal = (user: UserType) => {
    setSelectedUser(user);
    setResetPasswordData({ password: '', confirmPassword: '' });
    setIsResetPasswordModalOpen(true);
    setError('');
  };

  // Open Delete Modal
  const openDeleteModal = (user: UserType) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
    setError('');
  };

  // Form validation
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) errors.name = t.nameRequired;
    if (!formData.nameEn.trim()) errors.nameEn = t.nameEnRequired;
    if (!formData.username.trim()) errors.username = t.usernameRequired;
    
    if (!isEditMode && !formData.password.trim()) {
      errors.password = t.passwordRequired;
    }
    
    if (formData.role === 'branch' && !formData.branch) {
      errors.branch = t.branchRequired;
    }
    
    if (formData.role === 'chef' && !formData.department) {
      errors.department = t.departmentRequired;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t.requiredFields);
      return;
    }
    
    try {
      const userData = {
        name: formData.name.trim(),
        nameEn: formData.nameEn.trim(),
        username: formData.username.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        role: formData.role,
        branch: formData.role === 'branch' ? formData.branch : undefined,
        department: formData.role === 'chef' ? formData.department : undefined,
        isActive: formData.isActive,
        ...(isEditMode ? {} : { password: formData.password.trim() }),
      };
      
      if (isEditMode && selectedUser) {
        const response = await usersAPI.update(selectedUser.id || selectedUser._id!, userData);
        if (response.success) {
          toast.success(t.updated);
          setIsModalOpen(false);
          fetchData();
        }
      } else {
        const response = await usersAPI.create(userData);
        if (response.success) {
          toast.success(t.added);
          setIsModalOpen(false);
          fetchData();
        }
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      let errorMessage = isEditMode ? t.updateError : t.createError;
      
      if (err.response?.data?.message) {
        const message = err.response.data.message;
        if (message.includes('username') || message.includes('اسم المستخدم')) {
          errorMessage = t.usernameExists;
        } else if (message.includes('email') || message.includes('بريد')) {
          errorMessage = t.emailExists;
        } else {
          errorMessage = message;
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetPasswordData.password || !resetPasswordData.confirmPassword) {
      setError(t.passwordRequired);
      return;
    }
    
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    
    if (resetPasswordData.password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }
    
    try {
      if (selectedUser) {
        const response = await usersAPI.resetPassword(
          selectedUser.id || selectedUser._id!,
          resetPasswordData.password
        );
        
        if (response.success) {
          toast.success(t.passwordResetSuccess);
          setIsResetPasswordModalOpen(false);
          setResetPasswordData({ password: '', confirmPassword: '' });
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t.passwordResetError;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Handle delete user
  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await usersAPI.delete(selectedUser.id || selectedUser._id!);
      if (response.success) {
        toast.success(t.deleted);
        setIsDeleteModalOpen(false);
        fetchData();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t.deleteError;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field: string) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Toggle select user
  const toggleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select all users
  const selectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id || user._id!));
    }
  };

  // Role options for form
  const formRoleOptions = [
    { value: 'admin', label: t.admin },
    { value: 'branch', label: t.branch },
    { value: 'chef', label: t.chef },
    { value: 'production', label: t.production },
    { value: 'accountant', label: t.accountant },
  ];

  // Loading state
  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 p-4 md:p-6 ${isRtl ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {t.manage}
            </h1>
            <p className="text-gray-600 text-sm">
              {t.totalUsers}: <span className="font-semibold">{totalUsers}</span> {t.users}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              icon={RefreshCw}
              onClick={fetchData}
              className="text-gray-700 hover:text-gray-900"
              size="sm"
            >
              {t.refresh}
            </Button>
            
            {loggedInUser?.role === 'admin' && (
              <Button
                variant="primary"
                icon={Plus}
                onClick={openAddModal}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                {t.add}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className={`absolute top-1/2 transform -translate-y-1/2 ${isRtl ? 'right-3' : 'left-3'} text-gray-400 w-5 h-5`} />
            <Input
              value={searchTerm}
              onChange={handleSearch}
              placeholder={t.searchPlaceholder}
              className={`${isRtl ? 'pr-10' : 'pl-10'} py-2 text-sm`}
            />
          </div>

          {/* Role Filter */}
          <Select
            label={t.role}
            options={roleOptions}
            value={filterRole}
            onChange={handleRoleFilter}
            className="text-sm"
          />

          {/* Clear Filters */}
          {(searchTerm || filterRole !== 'all') && (
            <div className="flex items-end">
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                size="sm"
              >
                {t.clearFilters}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="mb-6">
          <Card className="bg-red-50 border-red-200">
            <div className="flex items-center gap-3 p-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
              <Button
                variant="ghost"
                onClick={() => setError('')}
                className="text-red-600 hover:text-red-700 ml-auto"
                size="sm"
              >
                ✕
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <UsersIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm || filterRole !== 'all' ? t.noMatch : t.noUsers}
          </h3>
          <p className="text-gray-600 text-sm mb-6">
            {searchTerm || filterRole !== 'all' ? t.noMatch : t.empty}
          </p>
          {loggedInUser?.role === 'admin' && (
            <Button
              variant="primary"
              icon={Plus}
              onClick={openAddModal}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t.addFirst}
            </Button>
          )}
        </Card>
      ) : (
        <>
          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <Card className="mb-4 p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-blue-700">
                    {selectedUsers.length} {t.selected}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedUsers([])}
                    className="text-blue-700 hover:text-blue-800"
                    size="sm"
                  >
                    {t.cancel}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="text-blue-700 border-blue-300 hover:bg-blue-100"
                    size="sm"
                  >
                    {t.export}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Users Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
            {filteredUsers.map((user) => {
              const roleColor = getRoleColor(user.role);
              const isSelected = selectedUsers.includes(user.id || user._id!);
              
              return (
                <motion.div
                  key={user.id || user._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card 
                    className={`relative overflow-hidden border hover:shadow-md transition-all duration-200 ${
                      isSelected ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-300' : 'border-gray-200'
                    }`}
                  >
                    {/* Selection Checkbox */}
                    {loggedInUser?.role === 'admin' && (
                      <div className="absolute top-3 right-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectUser(user.id || user._id!)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`p-2 rounded-lg ${roleColor.bg} ${roleColor.border}`}>
                          <RoleIcon role={user.role} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm truncate">
                            {isRtl ? user.name : user.nameEn || user.name}
                          </h3>
                          <p className="text-gray-500 text-xs truncate">@{user.username}</p>
                        </div>
                        <Badge 
                          variant={user.isActive ? "success" : "error"}
                          className="text-xs"
                        >
                          {user.isActive ? t.active : t.inactive}
                        </Badge>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 mb-4">
                        {user.email && (
                          <div className="flex items-center gap-2 text-xs">
                            <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 truncate">{user.email}</span>
                          </div>
                        )}
                        
                        {user.phone && (
                          <div className="flex items-center gap-2 text-xs">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">{user.phone}</span>
                          </div>
                        )}
                        
                        {user.lastLogin && (
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">
                              {formatDate(user.lastLogin, isRtl)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Role Badge */}
                      <div className="mb-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}>
                          <RoleIcon role={user.role} className="w-3 h-3" />
                          {t[user.role]}
                        </span>
                      </div>

                      {/* Actions */}
                      {loggedInUser?.role === 'admin' && (
                        <div className="flex gap-2 pt-3 border-t border-gray-100">
                          <Button
                            variant="ghost"
                            onClick={() => openProfileModal(user)}
                            className="flex-1 text-xs text-gray-600 hover:text-gray-900"
                            size="sm"
                          >
                            {t.view}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => openEditModal(user)}
                            className="flex-1 text-xs text-blue-600 hover:text-blue-700"
                            size="sm"
                          >
                            {t.edit}
                          </Button>
                          
                       
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                showInfo={true}
                totalItems={totalUsers}
                itemsPerPage={12}
              />
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? t.edit : t.add}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <Input
                label={t.name}
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                placeholder={t.namePlaceholder}
                required
                error={formErrors.name}
                dir={isRtl ? 'rtl' : 'ltr'}
              />
              
              <Input
                label={t.nameEn}
                value={formData.nameEn}
                onChange={(value) => setFormData({ ...formData, nameEn: value })}
                placeholder={t.nameEnPlaceholder}
                required
                error={formErrors.nameEn}
                dir={isRtl ? 'rtl' : 'ltr'}
              />
              
              <Input
                label={t.username}
                value={formData.username}
                onChange={(value) => setFormData({ ...formData, username: value })}
                placeholder={t.usernamePlaceholder}
                required
                error={formErrors.username}
                dir={isRtl ? 'rtl' : 'ltr'}
              />
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <Select
                label={t.role}
                options={formRoleOptions}
                value={formData.role}
                onChange={(value) => setFormData({ 
                  ...formData, 
                  role: value as any,
                  branch: value === 'branch' ? formData.branch : '',
                  department: value === 'chef' ? formData.department : ''
                })}
              />
              
              {formData.role === 'branch' && (
                <Select
                  label={t.branch}
                  options={branches.map(branch => ({
                    value: branch._id,
                    label: isRtl ? branch.name : branch.nameEn || branch.name
                  }))}
                  value={formData.branch}
                  onChange={(value) => setFormData({ ...formData, branch: value })}
                  placeholder={t.branchPlaceholder}
                  required
                  error={formErrors.branch}
                />
              )}
              
              {formData.role === 'chef' && (
                <Select
                  label={t.department}
                  options={departments.map(dept => ({
                    value: dept._id,
                    label: dept.name
                  }))}
                  value={formData.department}
                  onChange={(value) => setFormData({ ...formData, department: value })}
                  placeholder={t.departmentPlaceholder}
                  required
                  error={formErrors.department}
                />
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t.email}
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              placeholder={t.emailPlaceholder}
              type="email"
              dir={isRtl ? 'rtl' : 'ltr'}
            />
            
            <Input
              label={t.phone}
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value })}
              placeholder={t.phonePlaceholder}
              dir={isRtl ? 'rtl' : 'ltr'}
            />
          </div>

          {/* Password & Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!isEditMode && (
              <Input
                label={t.password}
                value={formData.password}
                onChange={(value) => setFormData({ ...formData, password: value })}
                placeholder={t.passwordPlaceholder}
                type={showPassword['password'] ? 'text' : 'password'}
                required
                error={formErrors.password}
                icon={showPassword['password'] ? EyeOff : Eye}
                onIconClick={() => togglePasswordVisibility('password')}
                dir={isRtl ? 'rtl' : 'ltr'}
              />
            )}
            
            <Select
              label={t.status}
              options={[
                { value: true, label: t.active },
                { value: false, label: t.inactive },
              ]}
              value={formData.isActive}
              onChange={(value) => setFormData({ ...formData, isActive: value === 'true' })}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              variant="primary"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isEditMode ? t.update : t.add}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              {t.cancel}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        title={t.resetPassword}
        size="sm"
      >
        <form onSubmit={handleResetPassword} className="space-y-6">
          <div className="space-y-4">
            <Input
              label={t.newPassword}
              value={resetPasswordData.password}
              onChange={(value) => setResetPasswordData({ ...resetPasswordData, password: value })}
              placeholder={t.newPasswordPlaceholder}
              type={showPassword['newPassword'] ? 'text' : 'password'}
              required
              icon={showPassword['newPassword'] ? EyeOff : Eye}
              onIconClick={() => togglePasswordVisibility('newPassword')}
              dir={isRtl ? 'rtl' : 'ltr'}
            />
            
            <Input
              label={t.confirmPassword}
              value={resetPasswordData.confirmPassword}
              onChange={(value) => setResetPasswordData({ ...resetPasswordData, confirmPassword: value })}
              placeholder={t.confirmPasswordPlaceholder}
              type={showPassword['confirmPassword'] ? 'text' : 'password'}
              required
              icon={showPassword['confirmPassword'] ? EyeOff : Eye}
              onIconClick={() => togglePasswordVisibility('confirmPassword')}
              dir={isRtl ? 'rtl' : 'ltr'}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              variant="primary"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t.reset}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsResetPasswordModalOpen(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              {t.cancel}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t.confirmDelete}
        size="sm"
      >
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 text-sm">{t.warning}</p>
                <p className="text-red-600 text-sm mt-1">{t.deleteWarning}</p>
              </div>
            </div>
          </div>

          {selectedUser && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getRoleColor(selectedUser.role).bg}`}>
                  <RoleIcon role={selectedUser.role} className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {isRtl ? selectedUser.name : selectedUser.nameEn || selectedUser.name}
                  </p>
                  <p className="text-gray-600 text-xs">@{selectedUser.username}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {t.delete}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Profile Modal */}
      <Modal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        title={t.profile}
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${getRoleColor(selectedUser.role).bg}`}>
                <RoleIcon role={selectedUser.role} className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">
                  {isRtl ? selectedUser.name : selectedUser.nameEn || selectedUser.name}
                </h3>
                <p className="text-gray-600 text-sm">@{selectedUser.username}</p>
              </div>
              <Badge variant={selectedUser.isActive ? "success" : "error"}>
                {selectedUser.isActive ? t.active : t.inactive}
              </Badge>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label={t.email} value={selectedUser.email || '-'} />
              <DetailItem label={t.phone} value={selectedUser.phone || '-'} />
              <DetailItem label={t.role} value={t[selectedUser.role]} />
              <DetailItem 
                label={t.createdAt} 
                value={formatDate(selectedUser.createdAt, isRtl)} 
              />
              {selectedUser.lastLogin && (
                <DetailItem 
                  label={t.lastLogin} 
                  value={formatDate(selectedUser.lastLogin, isRtl)} 
                />
              )}
              <DetailItem 
                label={t.updatedAt} 
                value={formatDate(selectedUser.updatedAt, isRtl)} 
              />
            </div>

            {/* Branch Details */}
            {selectedUser.role === 'branch' && selectedUser.branch && (
              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">{t.branchDetails}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailItem 
                    label={t.branch} 
                    value={isRtl ? selectedUser.branch.name : selectedUser.branch.nameEn || selectedUser.branch.name} 
                  />
                  {selectedUser.branch.address && (
                    <DetailItem label={t.address} value={selectedUser.branch.address} />
                  )}
                  {selectedUser.branch.city && (
                    <DetailItem 
                      label={t.city} 
                      value={isRtl ? selectedUser.branch.city : selectedUser.branch.cityEn || selectedUser.branch.city} 
                    />
                  )}
                  {selectedUser.branch.phone && (
                    <DetailItem label={t.phone} value={selectedUser.branch.phone} />
                  )}
                </div>
              </div>
            )}

            {/* Department Details */}
            {selectedUser.role === 'chef' && selectedUser.department && (
              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">{t.department}</h4>
                <DetailItem label={t.department} value={selectedUser.department.name} />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setIsProfileModalOpen(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                {t.close}
              </Button>
              {loggedInUser?.role === 'admin' && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsProfileModalOpen(false);
                    openEditModal(selectedUser);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t.edit}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Helper Component
const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{value}</p>
  </div>
);

export default Users;